//#region Routes
import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { ContractResponse } from "@/models/api/contracts/Contract";
import { ContractComponentResponse } from "@/models/api/contracts/ContractComponent";
import { ContractViewModel } from "@/models/api/contracts/editor/ContractViewModel";
import { ContractorViewModel } from "@/models/api/contracts/editor/ContractorViewModel";
import { ContractComponentViewModelResponse } from "@/models/api/contracts/editor/ContractComponentViewModel";
import { OperationResult } from "@/models/api/orders/OperationResult";
import BaseCommand from "@/models/api/commands/BaseCommand";
import { axiosInstance } from "@/plugins/axios";
import { FileViewModel } from "@/models/api/contracts/editor/FileViewModel";
import { ContractByContractorResponse } from "@/models/api/contracts/ContractByContractor";
import { COST_DOCUMENT_TYPES } from "@/static/costDocumentTypes";

export const URL_GET_CONTRACTS_PAGED = "/Contract/Paged";
export const URL_POST_DELETE_CONTRACT = "/Contract/${ContractId}/Delete";
export const URL_GET_CONTRACTS_COMPONENTS_PAGED = "/Contract/${ContractId}/Component/Paged";

//Editor
export const URL_POST_ADD_CONTRACT = "/Contract/Add/${Type}";
export const URL_POST_EDIT_CONTRACT = "/Contract/Edit/${ContractId}";
export const URL_POST_UPDATE_CONTRACT_TEMPORARY = "/Contract/Temporary/Update";
export const URL_POST_UPLOAD_TEMPORARY_COMPONENT_FILE = "/Contract/Temporary/${OperationId}/Components/${TmpId}/Upload";
export const URL_POST_SAVE_CONTRACT = "/Contract/Save/${OperationId}";

export const URL_GET_TEMPORARY_CONTRACT = "/Contract/Temporary/${OperationId}";
export const URL_GET_TEMPORARY_CONTRACTOR = "/Contract/Temporary/${OperationId}/Contractor";
export const URL_GET_TEMPORARY_COMPONENTS = "/Contract/Temporary/${OperationId}/Components";
export const URL_GET_TEMPORARY_COMPONENT_FILE = "/Contract/Temporary/${OperationId}/Components/${TmpId}/File";

export const URL_GET_CONTRACTS_BY_CONTRACTOR = "/Contract/${ContractorId}/Paged";
export const URL_GET_COMPONENTS_FOR_COST_DOCUMENT = "/Contract/ComponentsForCostDocument/${ComponentId}";

//#endregion

const _getContracts = new CancellableHelper<ContractResponse>(URL_GET_CONTRACTS_PAGED, ContractResponse);
_getContracts.jsonSerializer = true;
export const getContracts = async (genericTableRequest: GenericTableRequest): Promise<ContractResponse> => {
  return await _getContracts.get(genericTableRequest);
};

const _deleteContract = new CancellableHelper<string>(URL_POST_DELETE_CONTRACT);
export const deleteContract = async (contractId: number): Promise<string> => {
  return await _deleteContract.post({ ContractId: contractId }, null);
};

const _getContractComponents = new CancellableHelper<ContractComponentResponse>(
  URL_GET_CONTRACTS_COMPONENTS_PAGED,
  ContractComponentResponse
);
_getContractComponents.jsonSerializer = true;
export const getContractComponents = async (
  genericTableRequest: GenericTableRequest,
  contractId: number
): Promise<ContractComponentResponse> => {
  return await _getContractComponents.get({ ...genericTableRequest, ContractId: contractId });
};

//Editor
const _addContract = new CancellableHelper<string>(URL_POST_ADD_CONTRACT);
export const addContract = async (type: COST_DOCUMENT_TYPES): Promise<string> => {
  return await _addContract.post({ Type: type });
};

const _editContract = new CancellableHelper<string>(URL_POST_EDIT_CONTRACT);
export const editContract = async (contractId: number): Promise<string> => {
  return await _editContract.post({ ContractId: contractId });
};

const _updateContractTemporary = new CancellableHelper<OperationResult>(
  URL_POST_UPDATE_CONTRACT_TEMPORARY,
  OperationResult
);
export const updateContractTemporary = async (command: BaseCommand): Promise<OperationResult> => {
  return await _updateContractTemporary.post(command);
};

export const uploadTemporaryComponentFile = async (
  operationId: string,
  tmpId: string,
  files: Array<File>
): Promise<OperationResult> => {
  const url = URL_POST_UPLOAD_TEMPORARY_COMPONENT_FILE.replace("${OperationId}", operationId).replace(
    "${TmpId}",
    tmpId
  );
  const formData = new FormData();
  for (const file of files) formData.append(file.name, file);
  return (
    await axiosInstance.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data as OperationResult;
};

const _saveContract = new CancellableHelper<number>(URL_POST_SAVE_CONTRACT);
export const saveContract = async (operationId: string): Promise<number> => {
  return await _saveContract.post({ OperationId: operationId });
};

const _getTemporaryContract = new CancellableHelper<ContractViewModel>(URL_GET_TEMPORARY_CONTRACT, ContractViewModel);
export const getTemporaryContract = async (operationId: string): Promise<ContractViewModel> => {
  return await _getTemporaryContract.get({ OperationId: operationId });
};

const _getTemporaryContractor = new CancellableHelper<ContractorViewModel>(
  URL_GET_TEMPORARY_CONTRACTOR,
  ContractorViewModel
);
export const getTemporaryContractor = async (operationId: string): Promise<ContractorViewModel> => {
  return await _getTemporaryContractor.get({ OperationId: operationId });
};

const _getTemporaryComponents = new CancellableHelper<ContractComponentViewModelResponse>(
  URL_GET_TEMPORARY_COMPONENTS,
  ContractComponentViewModelResponse
);
_getTemporaryComponents.jsonSerializer = true;
export const getTemporaryComponents = async (
  genericTableRequest: GenericTableRequest,
  operationId: string
): Promise<ContractComponentViewModelResponse> => {
  return await _getTemporaryComponents.get({
    OperationId: operationId,
    ...genericTableRequest,
  });
};

const _getTemporaryComponentFile = new CancellableHelper<FileViewModel>(
  URL_GET_TEMPORARY_COMPONENT_FILE,
  FileViewModel,
  true
);
export const getTemporaryComponentFile = async (operationId: string, tmpId: string): Promise<FileViewModel> => {
  return await _getTemporaryComponentFile.get({ OperationId: operationId, TmpId: tmpId });
};

const _getContractsByContractor = new CancellableHelper<ContractByContractorResponse>(
  URL_GET_CONTRACTS_BY_CONTRACTOR,
  ContractByContractorResponse
);
_getContractsByContractor.jsonSerializer = true;
export const getContractsByContractor = async (
  genericTableRequest: GenericTableRequest,
  contractorId: string
): Promise<ContractByContractorResponse> => {
  return await _getContractsByContractor.get({
    ContractorId: contractorId,
    ...genericTableRequest,
  });
};

const _getComponentsForCostDocument = new CancellableHelper<ContractComponentResponse>(
  URL_GET_COMPONENTS_FOR_COST_DOCUMENT,
  ContractComponentResponse
);
_getComponentsForCostDocument.jsonSerializer = true;
export const getComponentsForCostDocument = async (
  genericTableRequest: GenericTableRequest,
  componentId: number | null
): Promise<ContractComponentResponse> => {
  return await _getComponentsForCostDocument.get({ ...genericTableRequest, ComponentId: componentId });
};
